import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { ClearFinancingNegotiationsListAction, RefreshNegotiationInListAction, SearchFinancingNegotiationsListAction, SearchFinancingQuestionsListAction, UpdateFinancingQuestionAction, UpdateNegotiationInListAction } from '../actions/financing.actions';
import { catchError, tap } from 'rxjs/operators';
import { throwError } from 'rxjs';
import {PageableApiResult} from "../models/pagable-api-result.model";
import { IFinancingNegotiation, IFinancingNegotiationsFilters, IFinancingQuestion, IFinancingQuestionsFilters } from '../models/financing-negotiations.model';
import { NegotiationsApiService } from '../api/negotiations-api.service';

export interface FinancingNegotiationStateModel {
  negotiationsList: IFinancingNegotiation[];
  negotiationsPageableList: PageableApiResult<IFinancingNegotiation>;
  negotiationsFilters: IFinancingNegotiationsFilters;
  questionsList: IFinancingQuestion[];
  questionsPageableList: PageableApiResult<IFinancingQuestion>;
  questionsFilters: IFinancingQuestionsFilters;
}

const initialState: FinancingNegotiationStateModel = {
  negotiationsList: null,
  negotiationsPageableList: null,
  negotiationsFilters: null,
  questionsList: null,
  questionsPageableList: null,
  questionsFilters: null
};

const NEGOTIATIONS_STATE_TOKEN = new StateToken<FinancingNegotiationStateModel>('negotiations');

@State<FinancingNegotiationStateModel>({
  name: NEGOTIATIONS_STATE_TOKEN,
  defaults: initialState,
})
@Injectable()
export class NegotiationsState {
  constructor(private negotiationsApi: NegotiationsApiService) {}

  @Action(ClearFinancingNegotiationsListAction)
  handleClearFinancingNegotiationsListAction(ctx: StateContext<FinancingNegotiationStateModel>, action: ClearFinancingNegotiationsListAction) {
    ctx.patchState({
      negotiationsList: null,
      negotiationsPageableList: null,
      negotiationsFilters: null
    });
  }

  @Action(SearchFinancingNegotiationsListAction)
  handleSearchFinancingNegotiationsListAction(ctx: StateContext<FinancingNegotiationStateModel>, action: SearchFinancingNegotiationsListAction) {
    const filter = ctx.getState()?.negotiationsFilters;
    const actionFilter = action.payload.filter;

    let reqFilter: IFinancingNegotiationsFilters;
    if (filter || actionFilter) {
      reqFilter = {
        ...filter,
        ...actionFilter,
      };
    } else {
      reqFilter = {
        page: 0,
        size: 10,
      };
    }

    ctx.patchState({
      negotiationsFilters: reqFilter,
    });

    return this.negotiationsApi.getMyNegotiations(reqFilter).pipe(
      tap((lineas) => {
        const state = ctx.getState();
        let newContent = lineas.content || [];

        if (reqFilter.page > 0 && state.negotiationsPageableList?.content) {
          const currentContent = state.negotiationsPageableList.content;
          const existingIds = new Set(currentContent.map(c => c.id));
          const filteredNewContent = newContent.filter(c => !existingIds.has(c.id));
          
          newContent = [...currentContent, ...filteredNewContent];
        }
        ctx.patchState({
          negotiationsList: newContent,
          negotiationsPageableList: {
            ...lineas,
            content: newContent 
          },
        });
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  @Action(UpdateFinancingQuestionAction)
  handleUpdateFinancingQuestionAction(ctx:  StateContext<FinancingNegotiationStateModel>, action: UpdateFinancingQuestionAction) {
    const state = ctx.getState();
    const newQuestion = action.payload.question;

    const aplicarCambio = (question: IFinancingQuestion) =>
      question.id === newQuestion.id ? { ...question, ...newQuestion } : question;

    const pageableList = state?.questionsPageableList;

    ctx.patchState({
      questionsList: (state?.questionsList || []).map(aplicarCambio),
      // La grilla se alimenta de questionsPageableList, no de questionsList: sin
      // actualizar los dos, el cambio no llega a la pantalla.
      questionsPageableList: pageableList
        ? { ...pageableList, content: (pageableList.content || []).map(aplicarCambio) }
        : pageableList
    });
  }

  @Action(SearchFinancingQuestionsListAction)
  handleSearchFinancingQuestionsListAction(ctx: StateContext<FinancingNegotiationStateModel>, action: SearchFinancingQuestionsListAction) {
    const filter = ctx.getState()?.questionsFilters;
    const actionFilter = action.payload?.filter;

    let reqFilter: IFinancingQuestionsFilters;
    if (filter || actionFilter) {
      reqFilter = {
        ...filter,
        ...actionFilter,
      };
    } else {
      reqFilter = {
        page: 0,
        size: 10,
      };
    }

    ctx.patchState({
      questionsFilters: reqFilter,
    });

    return this.negotiationsApi.getAllMyQuestions(reqFilter).pipe(
      tap((lineas) => {
        const state = ctx.getState();
        ctx.patchState({
          questionsList: lineas.content,
          questionsPageableList: lineas,
        });
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  @Action(UpdateNegotiationInListAction)
  handleUpdateNegotiationInListAction(ctx: StateContext<FinancingNegotiationStateModel>, action: UpdateNegotiationInListAction) {
    // Refresca y reordena
    const state = ctx.getState();
    const updated = action.payload.negotiation;
    const lastMessage = updated.mensajes?.length
      ? updated.mensajes.reduce((latest, m) =>
          new Date(m.fechaCreacion) > new Date(latest.fechaCreacion) ? m : latest
        )
      : null;

    console.log('Updating negotiation in list:', updated);
    console.log('OfertaActivas:', updated.oferta ? true : false);
    const newContent = (state.negotiationsPageableList?.content ?? []).map(n =>
      n.id === updated.id
        ? {
            ...n,
            contenidoMensaje: lastMessage?.contenidoFormateado || lastMessage?.file?.name,
            fechaCreacionMensaje: lastMessage?.fechaCreacion,
            messagesUnread: false,
            ofertaActivas: updated.oferta ? true : false,
            oferta: updated.oferta
          }
        : n
    );

    console.log('New content after update:', newContent);

    const index = newContent.findIndex(n => n.id === updated.id);
    if (index > 0) {
      const [item] = newContent.splice(index, 1);
      newContent.unshift(item);
    }

    ctx.patchState({
      negotiationsPageableList: {
        ...state.negotiationsPageableList,
        content: newContent
      }
    });
  }

  @Action(RefreshNegotiationInListAction)
  handleRefreshNegotiationInListAction(ctx: StateContext<FinancingNegotiationStateModel>, action: RefreshNegotiationInListAction) {
    // Refresca sin reordenar
    const state = ctx.getState();
    const updated = action.payload.negotiation;
    const lastMessage = updated.mensajes?.length
      ? updated.mensajes.reduce((latest, m) =>
          new Date(m.fechaCreacion) > new Date(latest.fechaCreacion) ? m : latest
        )
      : null;

    console.log('Updating negotiation in list:', updated);
    console.log('OfertaActivas en refresh:', updated.oferta ? true : false);
    const newContent = (state.negotiationsPageableList?.content ?? []).map(n =>
      n.id === updated.id
        ? {
            ...n,
            contenidoMensaje: lastMessage?.contenidoFormateado || lastMessage?.file?.name,
            fechaCreacionMensaje: lastMessage?.fechaCreacion,
            messagesUnread: updated.messagesUnread,
            ofertaActivas: updated.oferta ? true : false,
            oferta: updated.oferta
          }
        : n
    );

    console.log('New content after refresh:', newContent);

    ctx.patchState({
      negotiationsPageableList: {
        ...state.negotiationsPageableList,
        content: newContent
      }
    });
  }

  @Selector()
  static getNegotiationsPage(state: FinancingNegotiationStateModel) {
    return state?.negotiationsPageableList;
  }

  @Selector()
  static getQuestionsPage(state: FinancingNegotiationStateModel) {
    return state?.questionsPageableList;
  }
}
